import {computed, GATracker, obtainActiveLocale, onUnmounted, ref} from "../.nuxt/imports";
import {useNuxtApp, navigateTo, useRoute} from "#app";
import {watch} from "../.nuxt/imports";
import PlaceCountry from "../lib/models/PlaceCountry";
import PlaceTouristRegion from "../lib/models/PlaceTouristRegion";
import {parseCurrentURL} from "../lib/functions";
import {onMounted} from "vue";
import {TyrionApiResponse} from "../lib/interfaces/TyrionApiResponse";
import {IBaseFilterData} from "../lib/interfaces/IBaseFilterData";


export function useBaseFilter() {
    const submitting = ref<boolean>(false);
    const dateRange = ref({startDate: null, endDate: null});
    const placeObj = ref<{ id: number, type: string, title: string } | null>(null);
    const searchTimer = ref<ReturnType<typeof setTimeout> | null>(null);
    const searchingPlace = ref<boolean>(false);
    const countries = ref<Array<PlaceCountry>>([]);
    const regions = ref<Array<PlaceTouristRegion>>([]);
    const place = ref<string>('');
    const {GATrackEvent} = GATracker();
    const filterStep = ref<string | null>(null);
    const filterData = ref<IBaseFilterData>({region: [], country: []});
    const place_previous = ref<string>('xxx');
    const places = ref<{
        cities: Array<{ id: string, title: string, type: string }>,
        districts: Array<{ id: string, title: string, type: string }>,
        touristic_regions: Array<{ id: string, title: string, langtitle: { [k: string]: string } }>
    } | null>(null);
    const hosts = ref({adult: 2, child: 0, newborn: 0, rooms: 1});

    const hostsCount = computed(() => {
        return hosts.value.adult + hosts.value.child + hosts.value.newborn;
    });

    const langCode = obtainActiveLocale();
    const activeCountry = obtainActiveLocale();
    const {$tyrionApi} = useNuxtApp();


    watch(place, () => {
        searchPlace();
    })

    onMounted(() => {
        setParamsFromUrl();
    });

    onUnmounted(() => {
        if (searchTimer.value !== null) clearTimeout(searchTimer.value);
    })

    const openAccommodation = (accommodation: { link: string, countryCode: string }) => {
        navigateTo(`/${langCode.code}-${accommodation.countryCode}/${accommodation.link}`);
    }


    const searchBase = () => {

        let fPlace = null;
        if (placeObj.value && placeObj.value.id > 0) {
            fPlace = [placeObj.value];
        } else if (filterData.value.region.length > 0) {
            fPlace = filterData.value.region.map((c) => {
                return {
                    type: c.type === 'country' ? 'country' : 'tourist_region',
                    id: c.type === 'country' ? c.id_country : c.id
                }
            });
        } else if (filterData.value.country) {
            fPlace = filterData.value.country.map((c) => {
                return {type: 'country', id: c.id}
            });
        }

        $tyrionApi.postTyrionUrl('/api3/accommodation/redirect', {
            body: {
                place: fPlace,
                dateFrom: dateRange.value.startDate,
                dateTo: dateRange.value.endDate,
                hosts: hosts.value,
                locale: langCode.code,
                country: activeCountry.code,
            }
        }).then((res) => {
            if (res.status === 'success') {
                if (window.location.pathname === res.data.url.substring(0, res.data.url.indexOf('?'))) {
                    navigateTo(res.data.url, {external: true});
                } else {
                    navigateTo(res.data.url);
                }

            } else {
                alert(res.message);
                submitting.value = false;
            }
        }).catch((e) => {
            const msg = e.response && e.response.data ? e.response.data.message : e.message;
            alert(msg);
            submitting.value = false;
        });


        GATrackEvent({
            event: 'search_accommodation',
            language: langCode.code,
            action: 'click',
            label: 'Search accommodations',
            value: 'index-filter',
        });

    };

    const searchPlace = () => {
        if (searchTimer.value !== null) clearTimeout(searchTimer.value);

        if (place.value && place.value.length > 0 && place.value_previous !== place.value) {
            searchTimer.value = setTimeout(() => {
                places.value = null;
                searchingPlace.value = true;
                place_previous.value = place.value;
                $tyrionApi.postTyrionUrl(`/api3/places/search`, {
                        body: {name: place.value},
                        cacheTime: (60 * 60 * 5)
                    }
                ).then((tplaces) => {
                    places.value = tplaces.data as unknown as {
                        [k: string]: Array<{ id: string, title: string, type: string }>
                    };
                }).finally(() => {
                    searchingPlace.value = false;
                });
            }, 450);
        } else {
            places.value = null;
            searchingPlace.value = false;
        }
    };

    const listCountry = () => {
        if (countries.value.length === 0 && searchingPlace.value === false) {
            searchingPlace.value = true;

            places.value = null;
            return $tyrionApi.postTyrionUrl(`/api3/places/list`, {
                    body: {type: 'country'},
                    cacheTime: (60 * 60 * 24)
                }
            ).then((tplaces: TyrionApiResponse) => {
                countries.value = (tplaces.data as Array<{
                    [k: string]: any
                }>).map(p => (new PlaceCountry()).fromObject(p)) as PlaceCountry[];
                return true;
            }).finally(() => {
                searchingPlace.value = false;
            });
        } else {
            return new Promise((resolve, reject) => reject);
        }
    };
    const listRegions = (country: Array<PlaceCountry>) => {
        if (searchingPlace.value === false) {
            regions.value = [];
            if (searchTimer.value !== null) clearTimeout(searchTimer.value);


            places.value = null;
            searchingPlace.value = true;
            $tyrionApi.postTyrionUrl(`/api3/places/list`, {
                    body: {type: 'tourist_region', country: country.map(c => c.id)},
                    cacheTime: (60 * 60 * 24)
                }
            ).then((tplaces: TyrionApiResponse) => {
                regions.value = (tplaces.data as Array<{
                    [k: string]: any
                }>).map(p => (new PlaceTouristRegion()).fromObject(p)) as PlaceTouristRegion[];
            }).finally(() => {
                searchingPlace.value = false;
            });
        }

    };

    const setParamsFromUrl = () => {
        const urlParams = parseCurrentURL(useRoute().fullPath) as { [k: string]: string | number | Array<{}> };
        hosts.value.adult = (Number(urlParams.p_adult) > 0) ? Number(urlParams.p_adult) : hosts.value.adult;
        hosts.value.child = (Number(urlParams.p_child) > 0) ? Number(urlParams.p_child) : hosts.value.child;
        hosts.value.newborn = (Number(urlParams.p_newborn) > 0) ? Number(urlParams.p_newborn) : hosts.value.newborn;
        hosts.value.rooms = (Number(urlParams.p_room) > 0) ? Number(urlParams.p_room) : hosts.value.rooms;
        if (urlParams.dateFrom) {
            // @ts-ignore
            dateRange.value.startDate = new Date(urlParams.dateFrom);
            // @ts-ignore
            dateRange.value.endDate = new Date(urlParams.dateTo);
        }
        if (urlParams.places && Array.isArray(urlParams.places)) {
            listCountry().then(() => {
                $tyrionApi.postTyrionUrl('/api3/places/obtainPlacesById', {
                    body: {
                        cacheTime: (8 * 60 * 60),
                        places: (urlParams.places as Array<{ type: string, id: number }>).map(p => {
                            return {
                                type: p.type,
                                id: p.id
                            }
                        })
                    }
                }).then(res => {
                    if (res.status === 'success') {
                        const places = res.data;
                        if (places && places.length > 0) {
                            places.forEach((place: {
                                type: string,
                                title: string,
                                id: number,
                                country: { id: number, title: number }
                            }) => {
                                if (place.type === 'tourist_region') {
                                    //countries.value.push(place.country);
                                    filterData.value.region.push({...place, ...{type: 'tourist_region'}});
                                    filterData.value.country.push({...place.country, ...{type: 'country'}});
                                } else if (['district', 'city', 'region'].some(c => (place.type === c))) {
                                    placeObj.value = place;
                                } else if (place.type === 'country') {
                                    //countries.value.push(place);
                                    filterData.value.region.push({type: 'country', id_country: place.id, id: place.code});
                                    filterData.value.country.push({...place, ...{type: 'country'}});
                                }
                            });

                            if (filterData.value.region.length > 0) {
                                //filterData.value.country = Object.values(countries);
                            }
                        }
                    }
                })
            });
        }
    }


    const toggleMultiple = (key: string, item: { id: number, title: string }) => {
        placeObj.value = null;
        if (filterData.value[key].some((i: { id: number }) => i.id === item.id)) {
            filterData.value[key] = filterData.value[key].filter((i: { id: number }) => i.id !== item.id);
        } else {
            filterData.value[key].push(item);
        }
    }


    const totalCounter = computed(() => {
        let counter = 0;
        if (filterStep.value === 'location.country') {
            filterData.value.country.forEach((c) => {
                counter += c.accommodation_count;
            })
        } else if (filterStep.value === 'location.region') {
            filterData.value.region.forEach((c) => {
                counter += c.accommodation_count;
            })
        }
        return counter;
    });

    return {
        place,
        searchPlace,
        submitting,
        filterData,
        totalCounter,
        filterStep,
        toggleMultiple,
        searchBase,
        searchingPlace,
        placeObj,
        places,
        dateRange,
        countries,
        regions,
        listCountry,
        listRegions,
        openAccommodation,
        hostsCount,
        hosts,
    }
}



